import React from 'react';
import { View } from 'react-native';

import {
  ErrorScreen,
  IconTypes,
  Icon,
  VFText,
  VFButton,
  Card,
} from '@vfgroup-oneplatform/foundation/Components';
import { ShimmerWrapper } from '@vfgroup-oneplatform/foundation/Components/Shimmer';
import { withTheme, LightThemeColors } from '@vfgroup-oneplatform/foundation/Components/Themes';
import ContentManager from '@vfgroup-oneplatform/foundation/ContentManager';
import { ViewPropTypes, TextPropTypes } from 'deprecated-react-native-prop-types';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import { Images } from '../../Themes';
import { BillingTrackingManager } from '../../Utils';
import BillingTestKeys from '../../Utils/BillingTestKeys.js';
import { Bill_Status, getBillStatusName } from '../../Utils/BillStatus.Enum';
import Tag from '../Tag/Tag';
import VfView from '../VfView';

import { paymentInfoLoadingConfig, buttonLoader } from './LoadingConfigurations/PaymentInfoLoading.config';
import styles from './PaymentInfo.Styles';

const trackOnPaymentPress = (
  buttonTitle,
  buttonPlaceholders,
  billStatus,
  billValue,
  billEndDate,
  getAdditionalTrackingInfo,
) => {
  const additionalData = _.isFunction(getAdditionalTrackingInfo) && getAdditionalTrackingInfo();
  const buttonTitleLocalized = ContentManager.translate(buttonTitle, buttonPlaceholders);
  BillingTrackingManager.trackEvent('ui_interaction', {
    event_action: 'OnClick',
    event_category: 'Button',
    event_label: buttonTitleLocalized,
    page_name: 'Billing:BillDetails',
    page_section: 'Billing:BillDetails',
    visitor_bill_amount_current_active: billValue,
    visitor_bill_date_due_current_active: moment(billEndDate).format('YYYYMMDD'),
    transaction_status: getBillStatusName(billStatus),
    ...additionalData,
  });
};

function PaymentInfo({
  fullAutoPaymentDate,
  withAutoBillStatus,
  isAutoBillActivated,
  isCurrentBill,
  status,
  isLoading,
  codeLabel,
  codeValue,
  billNumberLabel,
  billNumberValue,
  paymentMethod,
  paymentMethodLabel,
  billStatusLabel,
  loader,
  paymentDatePlaceholder,
  price,
  onPaymentPress,
  remainingDays,
  withSecondaryButton,
  secondaryButtonTitle,
  onSecondaryButtonPress,
  withTertiaryButton,
  tertiaryButtonTitle,
  onTertiaryButtonPress,
  isError,
  onTryAgainPress,
  theme,
  renderFooter,
  getAdditionalTrackingInfo,
  value,
  billEndDate,
  primaryButtonType,
  primaryButtonStyle,
  primaryButtonTitle,
  primaryButtonTitleStyle,
  isPrimaryButtonDisabled,
  secondaryButtonType,
  secondaryButtonStyle,
  secondaryButtonTitleStyle,
  isSecondaryButtonDisabled,
  isSecondaryButtonLoading,
  tertiaryButtonType,
  tertiaryButtonStyle,
  tertiaryButtonTitleStyle,
  isTertiaryButtonDisabled,
  paymentMethodStyle,
  renderSectionAfterBillInfo,
  tagContainerStyle,
  tagTextStyle,
  withPayButton,
  customButtonsProps,
  bottomLineStyle,
  ...props
}) {
  const Loader = loader ? (
    loader
  ) : (
    <ShimmerWrapper
      isLoading={isLoading}
      loadingConfig={paymentInfoLoadingConfig}
      containerStyle={styles.loadingCardContainer(theme)}
    />
  );
  const isNegativeBill = value < 0;
  const _onPaymentPress = (..._props) => {
    _.isFunction(onPaymentPress) && onPaymentPress(status);
    trackOnPaymentPress(..._props, getAdditionalTrackingInfo);
  };

  const isShowTextCurrentBill = () => {
    return withAutoBillStatus && isCurrentBill && isAutoBillActivated;
  };

  const renderHintAutoBillText = () => {
    return (
      <View style={styles.titleleftContainer}>
        <Icon
          type={IconTypes.image}
          name={Images.ic_green_circle}
          size={17}
          style={styles.statusDotStyle}
        />

        <VFText
          i18nKey={'billing_current_bill_auto_bill_pay'}
          style={styles.title}
          type="primary"
          placeHolders={fullAutoPaymentDate}
        />
      </View>
    );
  };

  const renderCustomButtons = () => {
    return customButtonsProps.map((btnProps, index) => {
      return <VFButton key={index} {...btnProps} />;
    });
  };

  const renderBillStatusValue = () =>
    status === Bill_Status.VALIDATED || status === Bill_Status.ON_HOLD ? (
      <VFText
        style={styles.paymentValues(theme)}
        placeHolders={`${remainingDays}`}
        i18nKey="billing_dashboard_due_in_days"
      />
    ) : (
      <Tag
        containerStyle={[styles.tagStyle, tagContainerStyle]}
        textStyle={tagTextStyle}
        status={status}
      />
    );

  const renderPaymentMethod = () => {
    if (paymentMethodLabel) {
      return {
        key: paymentMethodLabel,
        value: paymentMethod ? `${paymentMethod}` : 'billing_payment_method_not_saved',
      };
    }
  };

  const paymentInfoSections = [
    {
      key: billStatusLabel,
      value: renderBillStatusValue(),
      keyTextProps: {},
    },
    renderPaymentMethod(),
    {
      key: codeLabel,
      value: codeValue,
      keyTextProps: { TestKey: BillingTestKeys.paymentInfoCardBillDateLabelText },
      valueTextProps: { TestKey: BillingTestKeys.paymentInfoCardBillDateValueText },
    },
    {
      key: billNumberLabel,
      value: billNumberValue,
      keyTextProps: { TestKey: BillingTestKeys.paymentInfoCardBillNumberLabelText },
      valueTextProps: { TestKey: BillingTestKeys.paymentInfoCardBillNumberValueText },
    },
  ].filter(sec => sec);

  const getPaymentInfoSections = _.get(props, 'getPaymentInfoSections', () => paymentInfoSections);

  const renderPayButtons = () => {
    if (status === Bill_Status.SENT) {
      return (
        <VFButton
          type={primaryButtonType || 'primary'}
          title={primaryButtonTitle || 'billing_details_pay_bill_now'}
          placeHolders={[price]}
          onPress={() =>
            _onPaymentPress('billing_details_pay_bill_now', [price], status, value, billEndDate)
          }
          testID="payBillBtn"
          textStyle={primaryButtonTitleStyle}
          style={primaryButtonStyle}
          disabled={isPrimaryButtonDisabled}
        />
      );
    }

    if (status === Bill_Status.SETTLED) {
      return (
        <VFButton
          type={primaryButtonType}
          textStyle={[styles.textStyle(theme), primaryButtonTitleStyle]}
          style={primaryButtonStyle}
          placeHolders={paymentDatePlaceholder}
          title={primaryButtonTitle || 'billing_details_bill_paid'}
          disabled={!_.isNil(isPrimaryButtonDisabled) ? isPrimaryButtonDisabled : true}
          testKey={BillingTestKeys.paymentInfoBillPaidBtn}
        />
      );
    }

    if (status === Bill_Status.PARTIALLY_PAID) {
      return (
        <VFButton
          type={primaryButtonType || 'primary'}
          title={primaryButtonTitle || 'billing_details_pay_amount_left'}
          placeHolders={[price]}
          onPress={() =>
            _onPaymentPress('billing_details_pay_amount_left', [price], status, value, billEndDate)
          }
          testID="payBillBtn"
          textStyle={primaryButtonTitleStyle}
          style={primaryButtonStyle}
          disabled={isPrimaryButtonDisabled}
        />
      );
    }

    if (status === Bill_Status.NEW && !isNegativeBill) {
      return (
        <VFButton
          type={primaryButtonType || 'primary'}
          title={primaryButtonTitle || 'billing_details_pay_bill_now'}
          placeHolders={[price]}
          onPress={() =>
            _onPaymentPress('billing_details_pay_bill_now', [price], status, value, billEndDate)
          }
          testID="payBillBtn"
          textStyle={primaryButtonTitleStyle}
          style={primaryButtonStyle}
          disabled={isPrimaryButtonDisabled}
        />
      );
    }
  };

  const renderPaymentInfoCard = () => {
    if (isLoading) {
      return Loader;
    }
    return isError ? (
      <ErrorScreen
        backgroundStyle={styles.errorBackgroundStyle}
        iconStyle={styles.errorIconStyle}
        iconName="ic_warning_mid"
        text="billing_payment_method_error_text"
        textButton="billing_payment_method_error_try_again_button"
        textStyle={styles.errorTextStyle}
        tryButtonContainerStyle={styles.errorTryButtonContainerStyle}
        onPress={onTryAgainPress}
      />
    ) : (
      <VfView justify="center" alignItems="center">
        <VfView xs={11}>
          {getPaymentInfoSections(paymentInfoSections).map((__, i) =>
            i % 2 !== 0 ? null : (
              <>
                <View style={styles.paymentContainer}>
                  {getPaymentInfoSections(paymentInfoSections)
                    .slice(i, i + 2)
                    .map(({ key, value: _value, keyTextProps, valueTextProps }, index) => (
                      <View style={styles.paymentItem}>
                        <VFText style={styles.paymentTags(theme)} i18nKey={key} {...keyTextProps} />
                        {typeof _value === 'string' ? (
                          <VFText
                            style={styles.paymentValues(theme)}
                            i18nKey={_value}
                            {...valueTextProps}
                          />
                        ) : (
                          _value
                        )}
                      </View>
                    ))}
                </View>
                <View style={[styles.lineBottom(theme), bottomLineStyle]} />
              </>
            ),
          )}

          {_.isFunction(renderSectionAfterBillInfo) && renderSectionAfterBillInfo()}

          {isShowTextCurrentBill() && renderHintAutoBillText()}

          <VfView style={styles.paymentStatus}>
            {withPayButton && showToBePaidBtn && (
              <VFButton
                type={primaryButtonType}
                textStyle={[styles.textStyle(theme), primaryButtonTitleStyle]}
                style={primaryButtonStyle}
                disabledStyle={styles.primaryButtonDisableStyle(theme)}
                placeHolders={paymentDatePlaceholder}
                title={primaryButtonTitle || 'billing_credit_card_paid_date'}
                disabled={!_.isNil(isPrimaryButtonDisabled) ? isPrimaryButtonDisabled : true}
                testKey={BillingTestKeys.paymentInfoCreditCardBillDateBtn}
              />
            )}

            {withPayButton && renderPayButtons()}

            {withSecondaryButton && <>
              {isSecondaryButtonLoading ? <View style={styles.buttonLoading(theme)}>
                <ShimmerWrapper
                  isLoading={isSecondaryButtonLoading}
                  loadingConfig={buttonLoader}
                />
              </View> :  <VFButton
                type={secondaryButtonType || 'secondary'}
                title={secondaryButtonTitle}
                onPress={() =>
                  _.isFunction(onSecondaryButtonPress) && onSecondaryButtonPress(status)
                }
                style={[styles.optionalButtonsStyle, secondaryButtonStyle]}
                textStyle={secondaryButtonTitleStyle}
                disabled={isSecondaryButtonDisabled}
              />}
            </>}

            {withTertiaryButton && (
              <VFButton
                type={tertiaryButtonType || 'secondary'}
                title={tertiaryButtonTitle}
                onPress={() => _.isFunction(onTertiaryButtonPress) && onTertiaryButtonPress(status)}
                style={[styles.optionalButtonsStyle, tertiaryButtonStyle]}
                textStyle={tertiaryButtonTitleStyle}
                disabled={isTertiaryButtonDisabled}
                testKey={BillingTestKeys.paymentInfoCardDownloadPDFBtn}
              />
            )}

            {renderCustomButtons()}

            {_.isFunction(renderFooter) && renderFooter()}
          </VfView>
        </VfView>
      </VfView>
    );
  };

  const showToBePaidBtn =
    isNegativeBill ||
    ![Bill_Status.SETTLED, Bill_Status.SENT, Bill_Status.PARTIALLY_PAID, Bill_Status.NEW].includes(
      status,
    );
  return <Card style={styles.cardStyle(theme)}>{renderPaymentInfoCard()}</Card>;
}

PaymentInfo.defaultProps = {
  theme: LightThemeColors,
  withPayButton: true,
  customButtonsProps: [],
};

PaymentInfo.propTypes = {
  status: PropTypes.oneOf(Object.values(Bill_Status)),
  isLoading: PropTypes.bool,
  codeLabel: PropTypes.string,
  codeValue: PropTypes.string,
  billNumberLabel: PropTypes.string,
  billNumberValue: PropTypes.string,
  paymentMethod: PropTypes.string,
  paymentMethodLabel: PropTypes.string,
  billStatusLabel: PropTypes.string,
  loader: PropTypes.node,
  paymentDatePlaceholder: PropTypes.array,
  price: PropTypes.string,
  onPaymentPress: PropTypes.func,
  remainingDays: PropTypes.number,
  withSecondaryButton: PropTypes.bool,
  secondaryButtonTitle: PropTypes.string,
  onSecondaryButtonPress: PropTypes.func,
  withTertiaryButton: PropTypes.bool,
  tertiaryButtonTitle: PropTypes.string,
  onTertiaryButtonPress: PropTypes.func,
  isError: PropTypes.bool,
  onTryAgainPress: PropTypes.func,
  theme: PropTypes.object,
  renderFooter: PropTypes.func,
  getAdditionalTrackingInfo: PropTypes.func,
  value: PropTypes.number,
  billEndDate: PropTypes.string,
  primaryButtonType: PropTypes.string,
  primaryButtonStyle: ViewPropTypes.style,
  primaryButtonTitle: PropTypes.string,
  primaryButtonTitleStyle: ViewPropTypes.style,
  isPrimaryButtonDisabled: PropTypes.bool,
  secondaryButtonType: PropTypes.string,
  secondaryButtonStyle: ViewPropTypes.style,
  secondaryButtonTitleStyle: ViewPropTypes.style,
  isSecondaryButtonDisabled: PropTypes.bool,
  tertiaryButtonType: PropTypes.string,
  tertiaryButtonStyle: ViewPropTypes.style,
  tertiaryButtonTitleStyle: ViewPropTypes.style,
  isTertiaryButtonDisabled: PropTypes.bool,
  paymentMethodStyle: ViewPropTypes.style,
  bottomLineStyle: ViewPropTypes.style,
  renderSectionAfterBillInfo: PropTypes.func,
  tagContainerStyle: ViewPropTypes.style,
  tagTextStyle: TextPropTypes.style,
  fullAutoPaymentDate: PropTypes.array,
  isAutoBillActivated: PropTypes.bool,
  isCurrentBill: PropTypes.bool,
  withAutoBillStatus: PropTypes.bool,
  withPayButton: PropTypes.bool,
  customButtonsProps: PropTypes.array,
};

export { PaymentInfo };
export default withTheme(PaymentInfo);
